/**
 * Shaping a ChatRequest into an orqestrator HTTP request.
 *
 * Kept separate from the client because it is pure: the camelCase-to-snake_case
 * mapping is the part most likely to drift as orqestrator's API moves, and it is
 * worth asserting on directly rather than through a mocked fetch.
 */

/**
 * The only endpoint used. `/chat` owns conversation history: omit
 * `conversation_id` to start a thread and it returns the new one, pass it back
 * to continue.
 *
 * `/orchestrate` is the alternative and it streams, which is tempting, but it
 * is stateless: `OrchestratorRequest` has no `conversation_id` field at all, so
 * a thread started there can never be resumed and every turn arrives with no
 * memory of the last. A chat that forgets is worse than one that does not type
 * out, so history wins and the reply arrives in one piece.
 *
 * `ChatResponseMode` is `Literal["sync"]` today; orqestrator's own comment says
 * streaming levels come later. When they land, this is where it changes.
 */
export var CHAT_PATH = "/api/v1/chat";

/** Translate to the wire shape, omitting optional keys rather than sending nulls. */
export function buildRequestBody(req, projectId) {
  var _req$responseMode;
  var body = {
    project_id: projectId,
    slug: req.slug,
    user_prompt: req.prompt,
    // `/chat` now accepts `sync` or `stream_text`. Sync stays the default so a
    // caller that just wants an answer gets one JSON body.
    response_mode: (_req$responseMode = req.responseMode) !== null && _req$responseMode !== void 0 ? _req$responseMode : "sync"
  };
  if (req.conversationId) body.conversation_id = req.conversationId;
  if (req.metadata) body.metadata = req.metadata;

  // Distinct from `metadata`: this is what the agent's platform tools run
  // against. Only sent when there is a workspace, because orqestrator rejects
  // an mcp_platform block without one.
  if (req.workspaceId) {
    var platform = {
      workspace_id: req.workspaceId
    };
    if (req.patientId) platform.patient_id = req.patientId;
    if (req.accountId) platform.account_id = req.accountId;
    body.mcp_metadata = {
      mcp_platform: platform
    };
  }
  if (req.variables || req.snippetOverrides) {
    var _req$variables, _req$snippetOverrides;
    body.prompt_context = {
      variables: (_req$variables = req.variables) !== null && _req$variables !== void 0 ? _req$variables : {},
      snippet_overrides: (_req$snippetOverrides = req.snippetOverrides) !== null && _req$snippetOverrides !== void 0 ? _req$snippetOverrides : {}
    };
  }
  return body;
}